(ns app.front.view.approval.table
  (:require [app.domain.approval.model :as approval-model]
            [app.domain.attachment.file-formats :as file-formats]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.file.upload :as upload]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [app.front.view.approval.filters :as approval-filters]
            [app.front.view.approval.form :as approval-form]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-event-fx ::approval-attachment-uploaded
  (fn [_ [_ process-target approval-id status response]]
    (let [file-id (ffirst response)]
      {:dispatch-n [[::entities/merge-entity-db status response]
                    [::process/signal
                     process-target
                     [:process.signal/add-approval-attachment (util/mmap approval-id file-id)]]
                    [::remove-pending-attachment file-id]]})))

(rf/reg-event-fx ::visibility-change.success
  (fn [_ [_ status response]]
    {:fx [[:dispatch [::entities/apply-changes status response]]
          [:dispatch [:notifications/add :toast :success :success :approval/visibility-updated]]]}))

(rf/reg-event-db ::add-pending-attachment
  (fn [db [_ id]]
    (update db ::pending-attachments set/union #{id})))

(rf/reg-event-db ::remove-pending-attachment
  (fn [db [_ id]]
    (update db ::pending-attachments disj id)))

(rf/reg-event-fx ::add-additional-comment
  (fn [_ [_ process-target approval-id comment]]
    {:dispatch [::process/signal
                process-target
                [:process.signal/add-approval-comment (util/mmap approval-id comment)]]}))

(defn- add-approval-attachment [process-target approval-id file-id filename content-type md5]
  (>evt [::add-pending-attachment file-id])
  (comm/send-command-execution
    {:command          :file/add-workspace-file
     :data             {:workspace-id      (-> process-target :entity/parent :workspace/id)
                        :file-id           file-id
                        :file/filename     filename
                        :file/content-type content-type
                        :file/category     :file.category/approval-attachment
                        :file/md5          md5
                        :async-pdfa?       true}
     :on-success       [::approval-attachment-uploaded process-target approval-id]
     :finally-dispatch [::remove-pending-attachment file-id]}))

(rf/reg-sub ::pending-attachments :-> ::pending-attachments)

(rf/reg-event-fx ::fetch-assignees.success
  (fn [{:keys [db]} [_ group-id status resp]]
    {:db       (-> db
                   (update ::fetching disj group-id))
     :dispatch [::entities/merge-entity-db status resp]}))

(rf/reg-event-fx ::fetch-assignees
  (fn [{:keys [db]} [_ group-id]]
    {:db           (-> db
                       (update ::fetching (fn [fetching-groups]
                                            (conj (or fetching-groups #{}) group-id))))
     :comm/command {:command    :group/assignees
                    :method     :get
                    :data       (util/mmap group-id)
                    :on-success [::fetch-assignees.success group-id]}}))

(rf/reg-sub ::fetching :-> ::fetching)

(rf/reg-sub ::fetching-assignees?
  :<- [::fetching]
  (fn [fetching [_ group-id]]
    (contains? fetching group-id)))


(rf/reg-event-db ::remove-approval-being-finished-by-auth
  (fn [db [_ id]]
    (update db ::approvals-being-finished-by-auth disj id)))


(rf/reg-sub ::approvals-being-finished-by-auth :-> ::approvals-being-finished-by-auth)


(rf/reg-sub ::approval-conditions
  :<- [::entities/context]
  (fn [ctx [_ approval-id]]
    (->> (entities/find-entities ctx {:entity/type   :entity.type/approval-condition
                                      :entity/parent approval-id})
         (sort-by (comp :ordered/order :approval-condition/condition)))))


(defn visibility-as-kw [approval]
  (let [perms (set (:entity/required-read-permissions approval))]
    (cond
      (contains? perms :permission.approval/stakeholder-read)
      :approval.visibility/stakeholders

      (contains? perms :permission.approval/applicant-read)
      :approval.visibility/applicants

      (contains? perms :permission.approval/read)
      :approval.visibility/default

      :else
      :approval.visibility/all)))

(defn assign-widget [tr process-target approval task *assign-visible?]
  (let [group-id          (-> task :task/user-group :db/id)
        stakeholder-users (<sub [::approval-form/stakeholder-users group-id])
        user-id           (or (and (-> task :task/assignees first :entity/type (= :entity.type/user))
                                   (-> task :task/assignees first :db/id))
                              :none)]
    [:div.flex.items-center
     [:h5.mr-2 (tr [:approval/assignee])]
     (if (<sub [::fetching-assignees? group-id])
       (cc/loading-spinner {:with-overlay false :small true})
       (cc/select
         {:id         (str (:db/id approval) "-re-assign")
          :class-name "full-width-select"
          :light      true
          :label-text (tr [:approval/assignee])
          :hide-label true
          :value      user-id
          :inline     true
          :on-change  (fn [e]
                        (reset! *assign-visible? false)
                        (>evt [::process/signal
                               process-target
                               [:process.signal/assign-approval {:approval-id    (:db/id approval)
                                                                 :target-user-id (-> e futil/tval js/parseInt)}]]))}
         (when (= user-id :none)
           [ui/placeholder-select-item tr :none])
         (futil/for-frag [user stakeholder-users]
           (cc/select-item {:text  (user-model/full-name user)
                            :value (:db/id user)}))))]))

(defn request-view [tr process-target approval task]
  (r/with-let [*assign-visible?  (r/atom false)
               *visibility-edit? (r/atom false)]
    (let [approval-id           (:db/id approval)
          visibility-kw         (visibility-as-kw approval)
          stakeholders-checked? (or (= visibility-kw :approval.visibility/stakeholders)
                                    (= visibility-kw :approval.visibility/all))
          applicants-checked?   (or (= visibility-kw :approval.visibility/applicants)
                                    (= visibility-kw :approval.visibility/all))]
      [:<>
       [:div.bx--row.items-center.leading-6
        [:h3.bx--col-xs-2.bx--col-md-3.m-0.font-semibold.text-sm
         (tr [:approval/description])]
        [:div.bx--col-xs-10.bx--col-md-9.flex.justify-end.items-center
         [:div.mr-2
          [:h3.m-0.font-semibold.text-sm (tr [:approval/visibility])]]
         (if @*visibility-edit?
           [:div.flex
            [approval-form/visibility-edit
             (util/mmap {:stakeholders-checked?  stakeholders-checked?
                         :applicants-checked?    applicants-checked?
                         :stakeholders-on-change #(>evt [:app.front.process/signal
                                                         process-target
                                                         [:process.signal/edit-approval-visibility
                                                          {:approval-id             approval-id
                                                           :visibility-stakeholders %
                                                           :visibility-applicants   applicants-checked?}]
                                                         {:on-success [::visibility-change.success]}])
                         :applicants-on-change   #(>evt [:app.front.process/signal
                                                         process-target
                                                         [:process.signal/edit-approval-visibility
                                                          {:approval-id             approval-id
                                                           :visibility-applicants   %
                                                           :visibility-stakeholders stakeholders-checked?}]
                                                         {:on-success [::visibility-change.success]}])}
                        tr approval-id)]
            [:div.flex.m-2
             (cc/icon {:icon        i/remove
                       :description (tr [:close])
                       :fill        "var(--ui-blue)"
                       :class-name  "cursor-pointer"
                       :on-click    #(swap! *visibility-edit? not)})]]
           [:div
            (tr [(visibility-as-kw approval)])
            (when (<sub [::process/signal-permitted?
                         (:db/id process-target)
                         [:process.signal/edit-approval-visibility
                          {:approval-id approval-id}]])
              [:span.m-2
               (cc/icon {:icon        i/pen
                         :description (tr [:edit])
                         :fill        "var(--ui-blue)"
                         :class-name  "cursor-pointer"
                         :on-click    #(swap! *visibility-edit? not)})])])
         (when (<sub [::process/app-signal-permitted?
                      [:process.signal/assign-approval {:approval-id approval-id}]])
           [:div.ml-6.approval-assign-button
            (if @*assign-visible?
              [assign-widget tr process-target approval task *assign-visible?]
              (cc/button
                {:kind     :tertiary
                 :size     :small
                 :on-click (fn [_]
                             (>evt [::fetch-assignees (-> task :task/user-group :db/id)])
                             (reset! *assign-visible? true))}
                (tr [(if (-> task :task/assignees first :entity/type (= :entity.type/user))
                       :re-assign
                       :assign)])))])]]
       [:div.bx--row.mt-3.mb-3
        [:div.bx--col-xs-12
         [ui-rt/editor {:initial-value (-> task :task/description)}]]
        [approval-form/ws-config-change-indicator (util/mmap {:attr :approval/creation-group-configs}
                                                             tr approval-id)]]])))

(defn approval-attachments [tr process-target approval]
  [:div.bx--row
   (when-let [pending (seq (<sub [::pending-attachments]))]
     [:div.bx--col-xs-12.centered-loading.mb-3
      (cc/inline-loading {:description (tr [:file/processing])
                          :success     (empty? pending)})])
   (futil/for-frag [file (<sub [::entities/entities {:entity/parent (:db/id approval)
                                                     :entity/type   :entity.type/file}])]
     [:div.bx--col-xs-12.bx--col-sm-6.bx--col-md-4
      [:div.approval-attachment
       [:div.flex.items-center
        (cc/icon {:icon        i/document
                  :description (tr [:entity.type/attachment])})
        [file-download/download-workspace-file-link file {:class "mx-2"}]
        (when (:file/conversion-results file)
          ;; Means that the file got converted to PDF/A
          [:span
           "["
           [file-download/download-workspace-file-link file {:text      (tr [:attachment/original-version])
                                                             :original? true}]
           "]"])]
       (let [signal [:process.signal/delete-approval-attachment {:approval-id (:db/id approval)
                                                                 :file-id     (:db/id file)}]]
         (when (<sub [::process/signal-permitted? process-target signal])
           [:div
            [ui/modal-confirmation-button
             {:tr                tr
              :id                "delete-attachment"
              :trigger-icon-opts {:icon        i/remove
                                  :class-name  "cursor-pointer"
                                  :description (tr [:approval/delete-attachment])}
              :on-yes-click      #(>evt [::process/signal process-target signal])}
             (tr [:approval/delete-att-confirm])]]))]])])

(defn- hiccup-paragraph [text]
  (futil/for-frag [line (str/split text #"\n")]
    [:<> line [:br]]))

(defn additional-comments [tr process-target approval]
  (r/with-let [*new-comment (r/atom "")]
    (let [comments         (:approval/additional-comments approval)
          approval-id      (:db/id approval)
          can-add-comment? (<sub [::process/signal-permitted?
                                  process-target
                                  [:process.signal/add-approval-comment {:approval-id approval-id}]])]
      [:<>
       (when (seq comments)
         [:div.border-b.border-solid.border-ui-gray.mb-2
          (futil/for-frag [comment comments]
            [:div.border-t.border-solid.border-ui-gray.py-2
             [hiccup-paragraph comment]])])                 ;; Add WS config change indicator at comment time here if needed

       (when can-add-comment?
         [:div.bx--row
          [:div.bx--col-xs-12.mt-2
           [ui/modal-confirmation-button
            {:id                  "add-approval-additional-comment-button"
             :tr                  tr
             :modal-title         (tr [:approval/add-additional-comment])
             :trigger-button-text (tr [:approval/add-additional-comment])
             :yes-button-text     (tr [:approval/add-additional-comment])
             :no-button-text      (tr [:cancel])
             :trigger-button-opts {:kind          :tertiary
                                   :size          :small
                                   :icon-position :right
                                   :icon          i/plus}
             :on-yes-click        #(do (>evt [::add-additional-comment process-target approval-id @*new-comment])
                                       (reset! *new-comment ""))
             :yes-disabled?       (str/blank? @*new-comment)}
            [ui/text-area {:label-text (tr [:approval/comment])
                           :value      @*new-comment
                           :on-change  #(reset! *new-comment (futil/tval %))}]]]])])))


(def result->select-tr {:approval.condition.result/cleared     :approval.condition.result/cleared-select
                        :approval.condition.result/not-cleared :approval.condition.result/not-cleared-select})


(def result->border-class {nil                                    "border-status-warning"
                           :approval.condition.result/cleared     "border-status-success"
                           :approval.condition.result/not-cleared "border-status-error"})

(def result->fill-var {nil                                    "var(--status-warning)"
                       :approval.condition.result/cleared     "var(--status-success)"
                       :approval.condition.result/not-cleared "var(--status-error)"})


(def result->icon {:approval.condition.result/cleared     i/check
                   :approval.condition.result/not-cleared i/remove})


(defn approval-condition-result-map [approval-conditions]
  (reduce
    (fn [m ac]
      (p/assoc-if m (:db/id ac) (select-keys ac [:approval-condition/result
                                                 :approval-condition/not-cleared-reason])))
    {}
    approval-conditions))


(defn approval-conditions [tr approval *data]
  (let [approval-conditions (<sub [::approval-conditions (:db/id approval)])
        results             (if *data
                              (:approval-condition-results @*data)
                              (approval-condition-result-map approval-conditions))]
    (when (seq approval-conditions)
      [:div>div.bx--row.mt-4>div.bx--col-xs-12
       [:div.mb-2.font-bold
        (tr [(if *data :approval/open-conditions :approval/conditions)])]
       [:div.bx--row
        [:div.bx--col-xs-12.bx--col-xl-10.bx--col-xxl-8
         #_{:clj-kondo/ignore [:deprecated-var]}
         [ui-table/responsive-table {:tr                 tr
                                     :class              "mt-4"
                                     :expandable?        true
                                     :headerless?        true
                                     :row-class-override "items-center p-4 bg-gray-200 font-bold"}
          [{:flex 6}
           {:flex 1}]
          (map (fn [{id :db/id condition :approval-condition/condition}]
                 {:key              id
                  :opts             {:class ["mb-2 border-solid border-l-4 rounded-md" (result->border-class (get-in results [id :approval-condition/result]))]}
                  :cols             [{:content [:span (str (-> condition :ordered/order inc)
                                                           ". "
                                                           (:application.condition/title condition))]}
                                     {:content (when-let [result (get-in results [id :approval-condition/result])]
                                                 [:div.flex.items-center.leading-tight
                                                  [:span.uppercase (tr [result])]
                                                  (cc/icon {:icon        (result->icon result)
                                                            :description ""
                                                            :class-name  "ml-2"
                                                            :fill        (result->fill-var result)})])}]
                  :expanded-content [:div.ml-2
                                     (if *data
                                       [:<> [:div.bx--row
                                             [ui-radio/radio {:name      (str "clear-approval-condition-" id)
                                                              :values    [:approval.condition.result/cleared :approval.condition.result/not-cleared]
                                                              :value     (get-in results [id :approval-condition/result])
                                                              :val->str  #(tr [(result->select-tr %)])
                                                              :on-change #(swap! *data update :approval-condition-results assoc-in [id :approval-condition/result] %)}]]
                                        (when (= (get-in results [id :approval-condition/result]) :approval.condition.result/not-cleared)
                                          [:div.bx--row.mt-4
                                           [ui/text-area
                                            {:id          (str id "-not-cleared-reason-textarea")
                                             :data-cy     "not-cleared-reason-textarea"
                                             :label-text  (tr [:approval/condition-not-cleared-reason])
                                             :value       (get-in results [id :approval-condition/not-cleared-reason])
                                             :on-change   #(swap! *data update :approval-condition-results assoc-in [id :approval-condition/not-cleared-reason] (futil/tval %))
                                             :placeholder (tr [:approval/condition-not-cleared-reason-placeholder])}]])]
                                       (when-let [reason (get-in results [id :approval-condition/not-cleared-reason])]
                                         [:div.bx--row.mt-4
                                          [:div.bx--col
                                           [:label.bx--label.bold (tr [:approval/condition-not-cleared-reason])]
                                           [:div reason]]]))
                                     [:div
                                      [:div.bx--row.mt-4
                                       [ui-rt/editor
                                        {:initial-value (:application.condition/body condition)}]]
                                      (when (:application.condition/closed condition)
                                        [:div.bx--row.mt-2
                                         [:div.relative.text-xs {:style {:top "-1px"}} (tr [:condition/closed]) " "
                                          [:span.font-bold (-> condition :application.condition/closed date-util/date)]
                                          " " (tr [:condition/by]) " "
                                          [:span.font-bold (-> condition :application.condition/closed-by user-model/full-name)]]])]]})
               approval-conditions)]]]])))


(rf/reg-event-db ::add-approval-being-finished-by-auth
  (fn [db [_ id]]
    (update db ::approvals-being-finished-by-auth set/union #{id})))


(defn- approval-comments [tr approval process-target]
  [:<>
   [:div.bx--row
    [:h5.bx--col-xs-12 (tr [:approval/stakeholder-comments])]]
   [:div.bx--row.my-3
    [:div.bx--col-xs-12
     (if (:approval/comments-in-attachment? approval)
       (tr [:approval/comments-in-attachment?])
       [hiccup-paragraph (:approval/response-text approval)])]]
   [additional-comments tr process-target approval]])


(defn completed-view [tr process-target approval task]
  (let [comments-allowed? (-> approval
                              :approval/result
                              (not= :approval.result/no-comments))]
    [:<>
     [request-view tr process-target approval task]
     [:div.approval-giver-form
      (when comments-allowed?
        [approval-comments tr approval process-target])

      [approval-conditions tr approval nil]

      (when comments-allowed?
        [approval-attachments tr process-target approval])

      [:div.flex.flex-1.flex-wrap.mt-6.gap-2.justify-between
       [approval-form/ws-config-change-indicator (util/mmap {:attr        :approval/completion-group-configs
                                                             :approval-id (:db/id approval)
                                                             :class       ""}
                                                            tr)]
       [:div.flex.justify-end
        (tr [:approval/by]
            [(or (some-> approval :approval/completed-by user-model/full-name)
                 (-> approval :latest-change :user))])
        ", "
        (-> approval :approval/completed ((<sub [::date-util/date-time-fn])))]]]]))


(def ^:private empty-comment-fields
  {:approval/comments-in-attachment? false
   :approval/response-text           ""})

(defn- comments-should-be-disabled-for-result? [approval-result]
  (contains? #{:none :approval.result/no-comments} approval-result))


(defn assignee-view [tr process-target approval task finished-by-auth?]
  (r/with-let [appr-conditions (approval-model/approval-conditions approval)
               initial-value   {:approval/result                  (or (:approval/result approval) :none)
                                :approval/comments-in-attachment? (or (:approval/comments-in-attachment? approval)
                                                                      false)
                                :approval/response-text           (or (:approval/response-text approval) "")
                                :approval-condition-results       (approval-condition-result-map appr-conditions)}
               *form-data      (r/atom initial-value)]
    (let [form-data                         @*form-data
          comments-should-be-disabled?      (comments-should-be-disabled-for-result? (:approval/result form-data))
          approval-conditions-not-resolved? (->> form-data
                                                 :approval-condition-results
                                                 vals
                                                 (some empty?)
                                                 true?)]
      [:<>
       [request-view tr process-target approval task]
       [:div.approval-giver-form
        [:div.bx--row
         [:div.bx--col-xs-12.bx--col-md-6.bx--col-lg-4
          [:div.flex.flex-col.justify-between.h-full
           (cc/select
             {:id         (str (:db/id approval) "-result")
              :class-name "full-width-select"
              :value      (:approval/result form-data)
              :label-text (tr [:approval/result])
              :on-change  #(let [approval-result-from-event (keyword "approval.result" (futil/tval %))]
                             (swap! *form-data
                                    merge
                                    {:approval/result approval-result-from-event}
                                    (when (comments-should-be-disabled-for-result? approval-result-from-event)
                                      empty-comment-fields)))}
             (when (= :none (:approval/result form-data))
               [ui/placeholder-select-item tr :none])
             (futil/for-frag [result (if (-> approval :approval/creation-state (= :state.application/submitted))
                                       approval-model/approval-results-in-submitted
                                       approval-model/approval-results)]
               (cc/select-item {:text  (tr [result])
                                :value result})))
           [:div.flex-grow-0
            (cc/checkbox {:id         (str (:db/id approval) "-comments-in-attachment")
                          :label-text (tr [:approval/comments-in-attachment?])
                          :checked    (:approval/comments-in-attachment? form-data)
                          :on-change  (fn [v]
                                        (swap! *form-data assoc
                                               :approval/comments-in-attachment? v
                                               :approval/response-text ""))
                          :disabled   comments-should-be-disabled?})]]]
         [:div.bx--col-xs-12.bx--col-md-6.bx--col-lg-8
          [:div
           [ui/text-area {:label-text (tr [:approval/comments])
                          :value      (:approval/response-text form-data)
                          :disabled   (or comments-should-be-disabled?
                                          (:approval/comments-in-attachment? form-data))
                          :on-change  #(swap! *form-data assoc :approval/response-text (futil/tval %))
                          :id         (str (:db/id approval) "-comment")}]]]]

        [approval-conditions tr approval *form-data]
        (when-not comments-should-be-disabled?
          [:<>
           [:div.bx--row.mt-3
            [:div.bx--col-xs-12
             [:div.mb-2.font-bold (tr [:approval/attachments])]
             [upload/upload-area
              (str (:db/id approval) "-file-upload")
              {:attach-file-fn (partial add-approval-attachment process-target (:db/id approval))
               :accept-types   file-formats/convertable-accept-value-for-input}]]]

           [approval-attachments tr process-target approval]

           (when-let [change-ts (-> approval :latest-change :ts)]
             [:div.bx--row.mt-6
              [:div.bx--col-xs-12.flex.justify-end
               (tr [:approval/last-update]
                   [(-> approval :latest-change :user)
                    ((<sub [::date-util/date-time-fn]) change-ts)])]])])

        [:div.bx--row.mt-6
         [:div.bx--col-xs-12.flex.justify-end
          (if finished-by-auth?
            (cc/button
              {:kind       :tertiary
               :class-name "mr-2"
               :on-click   #(>evt [::remove-approval-being-finished-by-auth (:db/id approval)])}
              (tr [:cancel]))
            (cc/button
              {:kind       :tertiary
               :class-name "mr-2"
               :disabled   (or
                             (= form-data initial-value)
                             approval-conditions-not-resolved?)
               :on-click   #(>evt [::process/signal
                                   process-target
                                   [:process.signal/edit-approval
                                    {:approval-id (:db/id approval)
                                     :changes     (p/condas-> form-data d
                                                    (= :none (:approval/result d)) (dissoc d :approval/result))}]])}
              (tr [:save])))
          (let [should-have-comments? (->> form-data
                                           :approval/result
                                           (not= :approval.result/no-comments))
                comments-are-missing? (let [comments-should-be-in-attachments? (:approval/comments-in-attachment? form-data)
                                            no-attachments?                    (empty? (<sub [::entities/entities {:entity/parent (:db/id approval)
                                                                                                                   :entity/type   :entity.type/file}]))
                                            no-text-in-comment-field?          (str/blank? (:approval/response-text form-data))]
                                        (if comments-should-be-in-attachments?
                                          no-attachments?
                                          no-text-in-comment-field?))]
            (cc/button
              {:kind     :primary
               :disabled (or (and should-have-comments?
                                  comments-are-missing?)
                             approval-conditions-not-resolved?)
               :on-click #(>evt [::process/signal
                                 process-target
                                 [:process.signal/edit-approval {:approval-id (:db/id approval)
                                                                 :changes     (merge {:domain/state :approval.state/complete}
                                                                                     form-data)}]])}
              (tr [:approval/finish])))]]]])))

(defn requester-label [tr approval]
  (when-let [creator (cond
                       (map? (:entity/creator approval))
                       (:entity/creator approval)

                       (= (:entity/creator approval)
                          (:db/id (<sub [:session/user])))
                       (<sub [:session/user]))]
    [:div (tr [:approval/by]
              [(user-model/full-name creator)])]))


(defn approval-table [{:keys [tr pre-selected-entity-id process-target] :as ctx}]
  (r/with-let [*sorting (r/atom [])]
    (let [approvals-being-finished (<sub [::approvals-being-finished-by-auth])
          tr-quiet                 (<sub [:tr/quiet])
          format-date              (<sub [::date-util/date-fn])
          sorted-approvals         (<sub [::approval-filters/sorted-approvals (:db/id process-target) @*sorting])
          show-delivery-status?    (and (<sub [::ff/enabled? :feature-flag.id/approval-delivery])
                                        (<sub [::process/signal-permitted? process-target [:process.signal/create-approval]]))]
      #_{:clj-kondo/ignore [:deprecated-var]}
      [ui-table/table {:expandable?          true
                       :tr                   tr
                       :default-expanded-key pre-selected-entity-id}
       (if (<sub [::ff/enabled? :feature-flag.id/circulation-table-sorting-and-filters])
         [[ui-table/table-header-sortable tr *sorting :approval/status]
          (when show-delivery-status? [ui-table/table-header-sortable tr *sorting :approval/delivery-status])
          [ui-table/table-header-sortable tr *sorting :approval/assigned-to]
          [:td ""]
          [ui-table/table-header-sortable tr *sorting :approval/request]
          [ui-table/table-header-sortable tr *sorting :approval/due-date]
          [ui-table/table-header-sortable tr *sorting :approval/completed]
          [:td ""]]
         [[:th (tr [:approval/status])]
          (when show-delivery-status? [:th (tr [:approval/delivery-status])])
          [:th (tr [:approval/assigned-to])]
          [:td ""]
          [:th (tr [:approval/request])]
          [:th (tr [:approval/due-date])]
          [:th (tr [:approval/completed])]
          [:td ""]])
       (futil/for-all [approval sorted-approvals
                       :let [approval-id              (:db/id approval)
                             task                     (<sub [::entities/entity {:entity/type    :entity.type/task
                                                                                :task/reference approval-id}])
                             edit-approval-permitted? (<sub [::process/signal-permitted?
                                                             process-target
                                                             [:process.signal/edit-approval (util/mmap approval-id)]])
                             edit-request-permitted?  (<sub [::process/signal-permitted?
                                                             process-target
                                                             [:process.signal/edit-approval-request (util/mmap approval-id)]])
                             finished-by-auth?        (contains? approvals-being-finished approval-id)]]
         {:expanded-content (cond
                              (du/in-state? :approval.state/complete approval)
                              [completed-view tr process-target approval task]

                              (and edit-request-permitted?
                                   (not finished-by-auth?))
                              ^{:key (str (:db/id approval) "-editor")}
                              [approval-form/edit-approval-form ctx approval {:finish-evt     [::add-approval-being-finished-by-auth approval-id]
                                                                              :process-target process-target}]

                              edit-approval-permitted?
                              [assignee-view tr process-target approval task finished-by-auth?]

                              :else
                              [request-view tr process-target approval task])
          :key              approval-id
          :cols             [{:content (case (:domain/state approval)
                                         :approval.state/complete
                                         [:<>
                                          [:div (tr [(:approval/result approval)])]]
                                         :approval.state/requested
                                         [:<>
                                          [:div (cond-> (str (tr [(:domain/state approval)]))
                                                  (:approval/creation-state approval)
                                                  (str " " (tr-quiet [(->> approval :approval/creation-state name (keyword "approval"))])))]]
                                         [:<>
                                          [:div (tr [(:domain/state approval)])]])}
                             (when show-delivery-status? {:content (tr [(or (approval-model/approval-delivery-status approval)
                                                                            :email-log.delivery-status/unknown)])})
                             {:content [:<>
                                        [:div (-> task :task/user-group :group/long-name)]
                                        (let [assignee (-> task :task/assignees first)]
                                          (when (-> assignee :entity/type (= :entity.type/user))
                                            [:div (user-model/full-name assignee)]))]}
                             {:content [:div
                                        (when (and (not= :task.state/ready (:domain/state task))
                                                   (-> task :task/due date-util/before-now?))
                                          [ui/tag {:color :yellow} (str/upper-case (tr [:overdue]))])]}
                             {:content [:<>
                                        [:div ((<sub [::date-util/date-time-fn]) (-> approval :entity/created))]
                                        [:div (requester-label tr approval)]]}
                             {:content (-> task :task/due format-date)}
                             {:content (when-let [completed (-> approval :approval/completed)]
                                         [:<>
                                          [:div (-> completed format-date)]
                                          [:div ((<sub [::date-util/time-fn]) completed)]
                                          (when (:approval/completion-state approval)
                                            (tr-quiet [(->> approval :approval/completion-state name (keyword "approval"))]))])}
                             {:content [:div.flex.flex-row.justify-end.bx--col
                                        (when (<sub [::process/signal-permitted?
                                                     process-target
                                                     [:process.signal/cancel-approval (util/mmap approval-id)]])
                                          [ui/modal-confirmation-button {:tr                  tr
                                                                         :id                  "cancel-request"
                                                                         :trigger-button-text (tr [:approval/cancel-request])
                                                                         :trigger-button-opts {:kind :tertiary
                                                                                               :size :small}
                                                                         :on-yes-click        #(>evt [::process/signal
                                                                                                      process-target
                                                                                                      [:process.signal/cancel-approval
                                                                                                       (util/mmap approval-id)]])}
                                           [:div (tr [:approval/cancel-request-confirm]
                                                     [(let [assignee (-> task :task/assignees first)]
                                                        (if (-> assignee :entity/type (= :entity.type/user))
                                                          (user-model/full-name assignee)
                                                          (-> task :task/user-group :group/long-name)))])]])]}]})])))
